import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";

interface FirebaseWebConfig {
  apiKey: string | null;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

const FALLBACK_CONFIG: FirebaseWebConfig = {
  apiKey: (import.meta.env["VITE_FIREBASE_API_KEY"] as string | undefined) ?? null,
  authDomain: "health-care-ai-aa9eb.firebaseapp.com",
  projectId: "health-care-ai-aa9eb",
  storageBucket: "health-care-ai-aa9eb.firebasestorage.app",
  messagingSenderId: "216997849771",
  appId: "1:216997849771:web:c355274898011e06d425ee",
  measurementId: "G-EF8FK0PLJ",
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let pending: Promise<Auth | null> | null = null;

/** Cached auth instance, if Firebase has already been initialised. */
export function getFirebaseAuth(): Auth | null {
  return auth;
}

/** Loads the publishable web config (server-held apiKey) and initialises Firebase Auth. */
export function ensureFirebaseAuth(): Promise<Auth | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (auth) return Promise.resolve(auth);
  if (pending) return pending;

  pending = (async () => {
    let config = FALLBACK_CONFIG;
    try {
      const res = await fetch("/api/public/firebase-config");
      if (res.ok) {
        const remote = (await res.json()) as FirebaseWebConfig;
        if (remote?.apiKey) config = remote;
      }
    } catch {
      // fall back to build-time env config
    }

    if (!config.apiKey) return null;

    app = getApps()[0] ?? initializeApp({ ...config, apiKey: config.apiKey });
    auth = getAuth(app);
    return auth;
  })();

  return pending;
}

const FIREBASE_ERRORS: Record<string, string> = {
  "auth/invalid-email": "That email address doesn't look right.",
  "auth/user-disabled": "This account has been disabled. Contact HealNova support.",
  "auth/user-not-found": "No account found with that email.",
  "auth/wrong-password": "Incorrect email or password.",
  "auth/invalid-credential": "Incorrect email or password.",
  "auth/email-already-in-use": "An account already exists with this email. Sign in instead.",
  "auth/weak-password": "Choose a password with at least 6 characters.",
  "auth/too-many-requests": "Too many attempts. Please wait a moment and try again.",
  "auth/network-request-failed": "Network error. Check your connection and try again.",
};

export function firebaseErrorMessage(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  if (code && FIREBASE_ERRORS[code]) return FIREBASE_ERRORS[code];
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}
